import React, { useEffect, useRef, useState } from "react";
import { Plus, Settings } from "lucide-react";
import { fmt } from "../../../../services/erpApi";
import CrmV2KanbanCard from "./CrmV2KanbanCard";
import CrmV2InlineCreateCard from "./CrmV2InlineCreateCard";
import { ODOO, stageBarColor } from "./crmV2Helpers";

const CrmV2KanbanColumn = ({
  stage,
  leads,
  total,
  loading,
  loadingMore,
  canManage,
  dragOver,
  draggingId,
  draggingStageId,
  dragOverStageId,
  userNameOf,
  onAddInColumn,
  onLoadMore,
  onDragOver,
  onDragLeave,
  onDrop,
  onStageDragStart,
  onStageDragEnd,
  onStageDragOver,
  onStageDragLeave,
  onStageDrop,
  onCardDragStart,
  onCardDragEnd,
  onCardClick,
  onFold,
  onSettings,
  showInlineCreate,
  inlineBusy,
  onInlineAdd,
  onInlineEdit,
  onInlineCancel,
  onCardAction,
}) => {
  const [headerHovered, setHeaderHovered] = useState(false);
  const [columnHovered, setColumnHovered] = useState(false);
  const sum = leads.reduce((s, l) => s + (Number(l.expected_revenue) || 0), 0);
  const remaining = Math.max(0, total - leads.length);
  const bar = stageBarColor(stage);
  const terminal = !!(stage.is_won || stage.is_lost);
  const isOriginColumn = draggingId != null && leads.some((l) => String(l.id) === String(draggingId));
  const isDraggingColumn = String(draggingStageId) === String(stage.id);
  const isStageDragOver = String(dragOverStageId) === String(stage.id) && !isDraggingColumn;
  const canStageDrag = canManage && !terminal;

  // Placeholder style — Odoo-like dashed semi-transparent
  const placeholderStyle = {
    height: 72,
    minHeight: 72,
    border: `2px dashed ${ODOO.link}`,
    borderRadius: 3,
    background: "rgba(0,135,132,0.06)",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: ODOO.link,
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.85,
    transition: "all 0.15s ease",
    pointerEvents: "none",
  };

  // Compteurs dragEnter/dragLeave pour éviter le glitch de flicker
  // Le dragLeave se déclenche aussi quand on survole un enfant (carte, bouton) — même logique pour le drag de colonnes
  const dragCounter = useRef(0);
  const stageDragCounter = useRef(0);

  // Reset compteurs quand les drags s'arrêtent (drop ou annulation)
  useEffect(() => {
    if (draggingId == null) dragCounter.current = 0;
  }, [draggingId]);
  useEffect(() => {
    if (draggingStageId == null) stageDragCounter.current = 0;
  }, [draggingStageId]);

  // Détection du type de drag via dataTransfer.types (défini dans CrmV2KanbanCard et handleStageDragStart)
  // Fallbacks : draggingStageId prop + flag global synchrone (pour le 1er dragEnter avant le setState React)
  const isStageDragEvent = (e) => {
    if (draggingStageId) return true;
    try { if (typeof window !== "undefined" && window.__kanbanStageDragging) return true; } catch {}
    try {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      // types peut être DOMStringList
      if (typeof types.includes === "function") return types.includes("application/x-stage");
      if (typeof types.contains === "function") return types.contains("application/x-stage");
      return Array.from(types).includes("application/x-stage");
    } catch { return false; }
  };

  const handleStageDragStartInner = (e) => {
    if (!canStageDrag) return;
    // Ignorer si le drag part d'une carte ou d'un bouton (on veut dragger la colonne entière, pas un enfant)
    try {
      const t = e.target;
      if (t.closest && (t.closest("[data-kanban-card]") || t.closest("button"))) return;
    } catch {}
    // Ignorer si un drag de lead est déjà en cours
    if (draggingId) return;
    try { if (typeof window !== "undefined") window.__kanbanStageDragging = true; } catch {}
    // Ghost pour l'effet papier de la colonne entière
    try {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const ghost = el.cloneNode(true);
      ghost.style.position = "absolute";
      ghost.style.top = "-1000px";
      ghost.style.left = "-1000px";
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${Math.min(rect.height, 480)}px`;
      ghost.style.margin = "0";
      ghost.style.transform = "rotate(2.5deg) scale(1.02)";
      ghost.style.boxShadow = "0 16px 36px rgba(0,0,0,0.20), 0 10px 18px rgba(0,0,0,0.12)";
      ghost.style.opacity = "0.96";
      ghost.style.pointerEvents = "none";
      ghost.style.overflow = "hidden";
      document.body.appendChild(ghost);
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      if (e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
      setTimeout(() => { if (ghost.parentNode) ghost.parentNode.removeChild(ghost); }, 0);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-stage", String(stage.id));
      e.dataTransfer.setData("text/plain", String(stage.id));
    } catch {}
    if (onStageDragStart) onStageDragStart(e, stage);
  };

  const handleStageDragEndInner = (e) => {
    stageDragCounter.current = 0;
    try { if (typeof window !== "undefined") window.__kanbanStageDragging = false; } catch {}
    if (onStageDragEnd) onStageDragEnd(e);
  };

  // Handlers unifiés : branchent vers lead ou stage selon le type
  const handleDragEnter = (e) => {
    e.preventDefault();
    if (isStageDragEvent(e)) {
      stageDragCounter.current += 1;
      if (onStageDragOver) onStageDragOver();
    } else {
      dragCounter.current += 1;
      onDragOver();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    if (isStageDragEvent(e)) {
      if (stageDragCounter.current === 0) {
        stageDragCounter.current = 1;
        if (onStageDragOver) onStageDragOver();
      }
    } else {
      if (dragCounter.current === 0) {
        dragCounter.current = 1;
        onDragOver();
      }
    }
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (isStageDragEvent(e)) {
      stageDragCounter.current -= 1;
      if (stageDragCounter.current <= 0) {
        stageDragCounter.current = 0;
        if (onStageDragLeave) onStageDragLeave();
      }
    } else {
      dragCounter.current -= 1;
      if (dragCounter.current <= 0) {
        dragCounter.current = 0;
        onDragLeave();
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isStageDragEvent(e)) {
      stageDragCounter.current = 0;
      if (onStageDrop) onStageDrop();
    } else {
      dragCounter.current = 0;
      onDrop();
    }
  };

  // Styles colonne — même spec que carte mais à l'échelle colonne entière
  const baseColumnShadow = "none";
  const hoverColumnShadow = "0 2px 10px rgba(0,0,0,0.06)";
  const draggingColumnShadow = "0 16px 36px rgba(0,0,0,0.20), 0 10px 18px rgba(0,0,0,0.12)";

  const columnStyle = isDraggingColumn
    ? {
        width: 280,
        minWidth: 280,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 220px)",
        background: "#fff",
        borderRadius: 4,
        border: `1px solid ${ODOO.primary}`,
        boxShadow: draggingColumnShadow,
        transform: "rotate(2.5deg) scale(1.02)",
        willChange: "transform, box-shadow",
        zIndex: 900,
        position: "relative",
        opacity: 0.96,
        cursor: "grabbing",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.15s ease, opacity 0.15s ease",
      }
    : {
        width: 280,
        minWidth: 280,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 220px)",
        background: dragOver ? "#eef6f5" : isStageDragOver ? "rgba(0,135,132,0.04)" : "transparent",
        borderRadius: 4,
        border: dragOver ? `2px dashed ${ODOO.link}` : isStageDragOver ? `2px dashed ${ODOO.link}` : "2px solid transparent",
        boxShadow: columnHovered && !dragOver && !isStageDragOver ? hoverColumnShadow : baseColumnShadow,
        transform: "rotate(0deg) scale(1)",
        willChange: "transform, box-shadow",
        zIndex: 1,
        position: "relative",
        opacity: 1,
        cursor: canStageDrag ? "grab" : "default",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease",
      };

  return (
    <div
      data-kanban-column={stage.id}
      draggable={canStageDrag}
      onDragStart={handleStageDragStartInner}
      onDragEnd={handleStageDragEndInner}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => setColumnHovered(true)}
      onMouseLeave={() => setColumnHovered(false)}
      style={columnStyle}
    >
      <div
        onMouseEnter={() => setHeaderHovered(true)}
        onMouseLeave={() => setHeaderHovered(false)}
        style={{ padding: "6px 4px 8px", flexShrink: 0 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            onClick={onFold}
            title="Fold stage"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 13,
              color: ODOO.text,
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              padding: 0,
              textAlign: "left",
              flex: 1,
              fontFamily: "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {stage.name}
          </button>
          {canManage && (
            <button
              type="button"
              onClick={onSettings}
              title="Stage settings"
              aria-label={`Settings for ${stage.name}`}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: ODOO.muted,
                padding: 2,
                display: "inline-flex",
                opacity: headerHovered ? 1 : 0,
                pointerEvents: headerHovered ? "auto" : "none",
                transition: "opacity .15s ease",
              }}
            >
              <Settings size={15} />
            </button>
          )}
          {canManage && !terminal && (
            <button
              type="button"
              onClick={onAddInColumn}
              title="Add"
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: ODOO.muted,
                padding: 2,
                display: "inline-flex",
              }}
            >
              <Plus size={16} />
            </button>
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: ODOO.muted, whiteSpace: "nowrap" }}>
            {fmt.moneyShort(sum)}
            {total > 0 && sum === 0 ? ` · ${total}` : total > leads.length ? ` · ${total}` : ""}
          </span>
        </div>
        <div style={{ height: 3, background: bar, borderRadius: 1, marginTop: 6 }} />
      </div>

      <div
        className="no-scrollbar"
        style={{
          flex: 1,
          overflowY: "auto",
          paddingRight: 2,
          minHeight: 80,
        }}
      >
        {showInlineCreate && (
          <CrmV2InlineCreateCard
            stageId={stage.id}
            busy={inlineBusy}
            onAdd={onInlineAdd}
            onEdit={onInlineEdit}
            onCancel={onInlineCancel}
          />
        )}

        {loading && leads.length === 0 && !showInlineCreate ? (
          <div style={{ fontSize: 12, color: ODOO.muted, textAlign: "center", padding: "24px 0" }}>Loading…</div>
        ) : leads.length === 0 && !showInlineCreate ? (
          dragOver && draggingId != null ? (
            <div style={placeholderStyle}>Déposer ici</div>
          ) : (
            <div
              style={{
                fontSize: 12,
                color: ODOO.muted,
                textAlign: "center",
                padding: "20px 8px",
                border: `1px dashed ${ODOO.border}`,
                borderRadius: 3,
                background: "#fff",
                transition: "border-color 0.15s ease, background 0.15s ease",
              }}
            >
              Drop here
            </div>
          )
        ) : (
          <>
            {/* Placeholder destination — Odoo-like dashed semi-transparent au point de drop */}
            {dragOver && draggingId != null && !isOriginColumn && (
              <div style={placeholderStyle}>Déposer ici</div>
            )}
            {leads.map((l) => {
              const isDragging = String(draggingId) === String(l.id);
              // La carte en cours de drag garde son animation (rotation 3deg, scale 1.03, shadow, grabbing, zIndex)
              // et transition 0.2s ease au drop. On ne duplique pas le placeholder à l'origine pour éviter
              // le double rendu ; le placeholder n'apparaît qu'à destination (spec).
              return (
                <CrmV2KanbanCard
                  key={l.id}
                  lead={l}
                  stage={stage}
                  stageName={stage.name}
                  userName={userNameOf?.(l.user_id)}
                  canManage={canManage && !terminal}
                  dragging={isDragging}
                  onDragStart={(e) => onCardDragStart(e, l)}
                  onDragEnd={onCardDragEnd}
                  onClick={() => onCardClick?.(l)}
                  onAction={onCardAction}
                />
              );
            })}
          </>
        )}

        {remaining > 0 && (
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            style={{
              width: "100%",
              marginTop: 4,
              marginBottom: 8,
              padding: "8px 10px",
              border: `1px solid #a8d5d4`,
              borderRadius: 3,
              background: "#fff",
              color: ODOO.link,
              fontSize: 13,
              fontWeight: 600,
              cursor: loadingMore ? "wait" : "pointer",
              fontFamily: "inherit",
            }}
          >
            {loadingMore ? "Loading…" : `Load more... (${remaining} remaining)`}
          </button>
        )}
      </div>
    </div>
  );
};

export default CrmV2KanbanColumn;
